import { clerkClient } from "@/App";
import { api } from "./api";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { Detection } from "@/store/detection-slice";
import { DateRange } from "react-day-picker";

interface DetectionFilters {
  startDate?: string;
  endDate?: string;
  droneId?: string;
  sensorId?: string;
}

interface DetectionsResponse {
  detections: Detection[];
  total: number;
}

const BATCH_SIZE = 1000;

// Fetch historical detections with optional filters
export const fetchDetectionsQuery = async (filters?: DetectionFilters) => {
  const authToken = await clerkClient.session?.getToken();
  
  // Build query parameters
  const params = new URLSearchParams();
  if (filters?.startDate) params.append("start_date", filters.startDate);
  if (filters?.endDate) params.append("end_date", filters.endDate);
  if (filters?.droneId) params.append("drone_id", filters.droneId);
  if (filters?.sensorId) params.append("sensor_id", filters.sensorId);
  
  const queryString = params.toString() ? `?${params.toString()}` : "";
  
  const resp = await api.get(`/v0/detections${queryString}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  return resp.data as DetectionsResponse;
};

export const useDetectionsQuery = (filters?: DetectionFilters) => {
  return useQuery<DetectionsResponse>({
    queryKey: ["detections", filters],
    queryFn: () => fetchDetectionsQuery(filters),
    enabled: true,
  });
};

// Fetch detections for a specific sensor
export const fetchDetectionsBySensorQuery = async (sensorId: string) => {
  const authToken = await clerkClient.session?.getToken();

  const resp = await api.get(`/v0/sensors/${sensorId}/detections`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  return resp.data as Detection[];
};

export const useDetectionsBySensorQuery = (sensorId: string) => {
  return useQuery({
    queryKey: ["sensor-detections", sensorId],
    queryFn: () => fetchDetectionsBySensorQuery(sensorId),
    enabled: !!sensorId,
  });
};

// Replay functionality
export const fetchDetectionBatch = async (
  startTime: string,
  endTime: string,
  offset: number
): Promise<DetectionsResponse> => {
  const authToken = await clerkClient.session?.getToken();
  
  const response = await api.get("/v0/detections", {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    params: {
      start_time: startTime,
      end_time: endTime,
      limit: BATCH_SIZE,
      offset,
      sortBy: "detection_time",
      sortOrder: "asc", // Important for replay chronological order
    },
  });
  
  return response.data;
};

export const useDetectionReplayQuery = (dateRange?: DateRange) => {
  return useInfiniteQuery<DetectionsResponse>({
    queryKey: ["detections-replay", dateRange?.from, dateRange?.to],
    queryFn: ({ pageParam = 0 }) =>
      dateRange?.from && dateRange?.to
        ? fetchDetectionBatch(
            dateRange.from.toISOString(),
            dateRange.to.toISOString(),
            pageParam as number
          )
        : Promise.resolve({ detections: [], total: 0 }),
    getNextPageParam: (lastPage, allPages) => {
      const fetchedCount = allPages.reduce((sum, page) => sum + page.detections.length, 0);
      return fetchedCount < lastPage.total ? fetchedCount : undefined;
    },
    initialPageParam: 0,
    enabled: !!dateRange?.from && !!dateRange?.to,
  });
};

export interface DetectionsQueryParams {
  limit?: number;
  offset?: number;
  sensor_id?: string;
  start_time?: string;
  end_time?: string;
  drone_identifier?: string;
}

export const fetchDetections = async (params: DetectionsQueryParams): Promise<DetectionsResponse> => {
  const authToken = await clerkClient.session?.getToken();
  
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.append("limit", params.limit.toString());
  if (params.offset) queryParams.append("offset", params.offset.toString());
  if (params.sensor_id) queryParams.append("sensor_id", params.sensor_id);
  if (params.start_time) queryParams.append("start_time", params.start_time);
  if (params.end_time) queryParams.append("end_time", params.end_time);
  if (params.drone_identifier) queryParams.append("drone_identifier", params.drone_identifier);
  
  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
  
  const resp = await api.get(`/v0/detections${queryString}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  return resp.data;
};

export const exportDetectionsToCSV = async (params: DetectionsQueryParams): Promise<Blob> => {
  const authToken = await clerkClient.session?.getToken();
  
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.append("limit", params.limit.toString());
  if (params.offset) queryParams.append("offset", params.offset.toString());
  if (params.sensor_id) queryParams.append("sensor_id", params.sensor_id);
  if (params.start_time) queryParams.append("start_time", params.start_time);
  if (params.end_time) queryParams.append("end_time", params.end_time);
  if (params.drone_identifier) queryParams.append("drone_identifier", params.drone_identifier);
  
  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
  
  const resp = await api.get(`/v0/detections/export${queryString}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    responseType: 'blob'
  });

  return resp.data;
};

export const useDetectionsQueryParams = (params: DetectionsQueryParams = {}) => {
  return useQuery({
    queryKey: ["detections", params],
    queryFn: () => fetchDetections(params),
  });
}; 